import { useTranslation } from 'react-i18next'

import { useShipmentFeed } from '#/entities/shipment'
import type { ShipmentFeedItem } from '#/entities/shipment'
import { HistoryIcon } from '#/shared/ui/icons'
import { Skeleton } from '#/shared/ui/skeleton'

import { FeedAutoLoader } from './feed-auto-loader'
import {
  ActivityHistoryRow,
  ContractHistoryRow,
  EmailHistoryRow,
  FileHistoryRow,
  NoteHistoryRow,
  PaymentHistoryRow,
  SmsHistoryRow,
  TaskHistoryRow,
} from './history-rows'

function FeedRowSkeleton() {
  return (
    <div className="flex h-9 items-center gap-2.5 rounded-md bg-white px-2.5">
      <Skeleton className="size-5 shrink-0 rounded-full" />
      <Skeleton className="h-4 flex-1" />
      <Skeleton className="h-3 w-24 shrink-0" />
    </div>
  )
}

function FeedSkeletonList() {
  return (
    <div className="flex w-full flex-col gap-2.5">
      <FeedRowSkeleton />
      <FeedRowSkeleton />
      <FeedRowSkeleton />
    </div>
  )
}

function FeedEmpty() {
  const { t } = useTranslation()
  return (
    <div className="flex animate-in fade-in slide-in-from-top-1 flex-col items-center gap-2 px-5 py-6 text-center duration-300 ease-out">
      <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
        <HistoryIcon className="size-5" />
      </div>
      <p className="text-sm font-medium text-gray-900">
        {t('orderDetails.history.empty.all.title')}
      </p>
      <p className="text-xs text-gray-500">
        {t('orderDetails.history.empty.all.hint')}
      </p>
    </div>
  )
}

function FeedRow({ item }: { item: ShipmentFeedItem }) {
  switch (item.kind) {
    case 'note':
      return <NoteHistoryRow note={item.data} />
    case 'task':
      return <TaskHistoryRow task={item.data} />
    case 'file':
      return <FileHistoryRow file={item.data} />
    case 'contract':
      return <ContractHistoryRow contract={item.data} />
    case 'sms':
      return <SmsHistoryRow message={item.data} />
    case 'email':
      return <EmailHistoryRow email={item.data} />
    case 'payment':
      return <PaymentHistoryRow payment={item.data} />
    default:
      return <ActivityHistoryRow activity={item.data} />
  }
}

interface AllHistoryFeedProps {
  shipmentId: string
}

export function AllHistoryFeed({ shipmentId }: AllHistoryFeedProps) {
  const { items, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useShipmentFeed(shipmentId)

  if (isLoading) return <FeedSkeletonList />
  if (items.length === 0) return <FeedEmpty />

  return (
    <div className="flex w-full flex-col gap-2.5">
      {items.map((item) => (
        <FeedRow key={`${item.kind}:${item.data.id}`} item={item} />
      ))}
      <FeedAutoLoader
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={fetchNextPage}
      />
    </div>
  )
}
